import jsPDF from "jspdf";
import html2canvas from "html2canvas";

/**
 * Export an HTML element to PDF
 * @param {string} elementId - The ID of the element to export
 * @param {string} filename - The filename for the PDF (without .pdf extension)
 * @param {Object} options - Additional options
 * @param {number} options.scale - Scale factor for the canvas (default: 2)
 * @param {string} options.format - Page format (default: 'a4')
 * @param {string} options.orientation - 'portrait' or 'landscape' (default: 'portrait')
 */
export async function exportToPDF(elementId, filename, options = {}) {
  const { scale = 2, format = "a4", orientation = "portrait" } = options;

  const element = document.getElementById(elementId);
  if (!element) {
    throw new Error(`Element with id "${elementId}" not found`);
  }

  // Temporarily show the element if hidden (for print-only content)
  const originalDisplay = element.style.display;
  if (originalDisplay === "none") {
    element.style.display = "block";
  }

  try {
    const canvas = await html2canvas(element, {
      scale,
      useCORS: true,
      logging: false,
      backgroundColor: "#ffffff",
      windowWidth: element.scrollWidth,
      windowHeight: element.scrollHeight,
    });

    const imgData = canvas.toDataURL("image/png");
    const pdf = new jsPDF(orientation, "mm", format);

    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pdfHeight = pdf.internal.pageSize.getHeight();

    const imgWidth = pdfWidth - 20; // 10mm margin on each side
    const imgHeight = (canvas.height * imgWidth) / canvas.width;

    // Handle multi-page PDFs if content is too tall
    let heightLeft = imgHeight;
    let position = 10; // Start 10mm from top
    const pageHeight = pdfHeight - 20; // Account for margins

    // Add first page
    pdf.addImage(imgData, "PNG", 10, position, imgWidth, imgHeight);
    heightLeft -= pageHeight;

    // Add additional pages if needed
    while (heightLeft > 0) {
      position = heightLeft - imgHeight + 10;
      pdf.addPage();
      pdf.addImage(imgData, "PNG", 10, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;
    }

    pdf.save(`${filename}.pdf`);
  } finally {
    // Restore original display style
    if (originalDisplay === "none") {
      element.style.display = originalDisplay;
    }
  }
}

/**
 * Print the current page (browser print dialog)
 */
export function printPage() {
  window.print();
}
